import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Warning,
  Description,
  FileDownload,
  Send,
  Assignment,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface ProviderComplianceStatus {
  _id: string;
  firstName: string;
  lastName: string;
  businessName: string;
  email: string;
  w9OnFile: boolean;
  w9TaxId?: string;
  tinValid: boolean;
  insuranceExpiryDate?: Date;
  insuranceVerified: boolean;
  idesReported: boolean;
  idesReportDate?: Date;
  idesConfirmationNumber?: string;
  contractGenerated: boolean;
  contractSigned: boolean;
  backupWithholdingEnabled: boolean;
  totalBackupWithheld: number;
  salesTaxThresholdMet: boolean;
  crt63CertificateSent: boolean;
  totalEarnings: number;
  needs1099: boolean;
  recentEarnings: number;
  needsContract: boolean;
  createdAt: Date;
}

const CompliancePage: React.FC = () => {
  const { user } = useAuth();
  const [providers, setProviders] = useState<ProviderComplianceStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [generate1099Dialog, setGenerate1099Dialog] = useState(false);
  const [generate1099Year, setGenerate1099Year] = useState(new Date().getFullYear());
  const [generating1099, setGenerating1099] = useState(false);
  const [sample1099Dialog, setSample1099Dialog] = useState<{ provider: ProviderComplianceStatus; year: number } | null>(null);
  const [sample1099Loading, setSample1099Loading] = useState(false);
  const [export1099PreparerDialog, setExport1099PreparerDialog] = useState(false);
  const [export1099PreparerYear, setExport1099PreparerYear] = useState(new Date().getFullYear());
  const [export1099PreparerLoading, setExport1099PreparerLoading] = useState(false);

  useEffect(() => {
    if (user && user.userType === 'admin') {
      fetchComplianceStatus();
    }
  }, [user]);

  const fetchComplianceStatus = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/compliance/status');
      if (response.success) {
        setProviders(response.data as ProviderComplianceStatus[]);
      } else {
        setError(response.error || 'Failed to fetch compliance status');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch compliance status');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate1099 = async () => {
    try {
      setGenerating1099(true);
      setError('');
      const response = await apiService.post(`/compliance/generate-1099/${generate1099Year}`);
      if (response.success) {
        const data = response.data as { generated: number; errors: Array<{ providerId: string; error: string }> };
        setSuccess(`Generated ${data.generated} 1099-NEC forms for ${generate1099Year}`);
        setGenerate1099Dialog(false);
        fetchComplianceStatus();
      } else {
        setError(response.error || 'Failed to generate 1099 forms');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to generate 1099 forms');
    } finally {
      setGenerating1099(false);
    }
  };

  const handleReportIDES = async (providerId: string) => {
    try {
      setError('');
      const response = await apiService.post(`/compliance/report-ides/${providerId}`);
      if (response.success) {
        const data = response.data as { confirmationNumber?: string };
        setSuccess(`Provider reported to IDES. Confirmation: ${data.confirmationNumber || 'N/A'}`);
        fetchComplianceStatus();
      } else {
        setError(response.error || 'Failed to report to IDES');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to report to IDES');
    }
  };

  const handleGenerateContract = async (providerId: string) => {
    try {
      setError('');
      const response = await apiService.post(`/compliance/generate-contract/${providerId}`);
      if (response.success) {
        setSuccess('Contract generated and sent to provider');
        fetchComplianceStatus();
      } else {
        setError(response.error || 'Failed to generate contract');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to generate contract');
    }
  };

  const handleEnableBackupWithholding = async (providerId: string) => {
    try {
      setError('');
      const response = await apiService.post(`/compliance/enable-backup-withholding/${providerId}`);
      if (response.success) {
        setSuccess('Backup withholding status updated');
        fetchComplianceStatus();
      } else {
        setError(response.error || 'Failed to update backup withholding');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update backup withholding');
    }
  };

  if (!user || user.userType !== 'admin') {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Access denied. Admin only.</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Compliance Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Monitor and manage IRS and Illinois compliance requirements for all providers
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Quick Actions */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                1099-NEC Generation
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Generate and send 1099-NEC forms for all providers who earned $600+ in a tax year
              </Typography>
              <Button
                variant="contained"
                startIcon={<Description />}
                onClick={() => setGenerate1099Dialog(true)}
              >
                Generate 2025 1099s
              </Button>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Compliance Summary
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Providers: {providers.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                W-9 On File: {providers.filter(p => p.w9OnFile).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                IDES Reported: {providers.filter(p => p.idesReported).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Contracts Signed: {providers.filter(p => p.contractSigned).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Backup Withholding Active: {providers.filter(p => p.backupWithholdingEnabled).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Export Options */}
      <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Button
          variant="outlined"
          startIcon={<FileDownload />}
          onClick={() => {
            // Export compliance data as CSV
            const csv = [
              ['Provider', 'Email', 'W-9 On File', 'TIN Valid', '1099 Needed', 'IDES Reported', 'Contract Signed', 'Backup Withholding', 'Total Withheld'].join(','),
              ...providers.map(p => [
                p.businessName || `${p.firstName} ${p.lastName}`,
                p.email,
                p.w9OnFile ? 'Yes' : 'No',
                p.tinValid ? 'Yes' : 'No',
                p.needs1099 ? `$${p.totalEarnings.toFixed(2)}` : 'No',
                p.idesReported ? 'Yes' : 'No',
                p.contractSigned ? 'Yes' : 'No',
                p.backupWithholdingEnabled ? 'Yes' : 'No',
                `$${p.totalBackupWithheld.toFixed(2)}`,
              ].join(','))
            ].join('\n');
            
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `compliance-export-${new Date().toISOString().split('T')[0]}.csv`;
            a.click();
            window.URL.revokeObjectURL(url);
          }}
        >
          Export Compliance Data (CSV)
        </Button>
        <Button
          variant="outlined"
          startIcon={<Assignment />}
          onClick={() => {
            setExport1099PreparerYear(new Date().getFullYear());
            setExport1099PreparerDialog(true);
          }}
        >
          Export for 1099 preparer
        </Button>
      </Box>

      {/* Provider Compliance Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Provider</TableCell>
              <TableCell>W-9</TableCell>
              <TableCell>TIN Valid</TableCell>
              <TableCell>1099 Status</TableCell>
              <TableCell>Insurance</TableCell>
              <TableCell>IDES</TableCell>
              <TableCell>Contract</TableCell>
              <TableCell>Backup Withholding</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {providers.map((provider) => (
              <TableRow key={provider._id}>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {provider.businessName || `${provider.firstName} ${provider.lastName}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {provider.email}
                  </Typography>
                </TableCell>
                <TableCell>
                  {provider.w9OnFile ? (
                    <Chip icon={<CheckCircle />} label="On File" color="success" size="small" />
                  ) : (
                    <Chip icon={<Cancel />} label="Missing" color="error" size="small" />
                  )}
                </TableCell>
                <TableCell>
                  {provider.tinValid ? (
                    <Chip label="Valid" color="success" size="small" />
                  ) : (
                    <Chip icon={<Warning />} label="Invalid" color="warning" size="small" />
                  )}
                </TableCell>
                <TableCell>
                  {provider.needs1099 ? (
                    <Chip label={`$${provider.totalEarnings.toFixed(2)}`} color="info" size="small" />
                  ) : (
                    <Chip label="< $600" color="default" size="small" />
                  )}
                </TableCell>
                <TableCell>
                  {provider.insuranceVerified ? (
                    <Chip label="Verified" color="success" size="small" />
                  ) : (
                    <Chip label="Not Verified" color="warning" size="small" />
                  )}
                </TableCell>
                <TableCell>
                  {provider.idesReported ? (
                    <Chip label="Reported" color="success" size="small" />
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleReportIDES(provider._id)}
                    >
                      Report
                    </Button>
                  )}
                </TableCell>
                <TableCell>
                  {provider.contractSigned ? (
                    <Chip label="Signed" color="success" size="small" />
                  ) : provider.needsContract ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Assignment />}
                      onClick={() => handleGenerateContract(provider._id)}
                    >
                      Generate
                    </Button>
                  ) : (
                    <Chip label="N/A" color="default" size="small" />
                  )}
                </TableCell>
                <TableCell>
                  {provider.backupWithholdingEnabled ? (
                    <Chip
                      label={`$${provider.totalBackupWithheld.toFixed(2)}`}
                      color="warning"
                      size="small"
                    />
                  ) : (
                    <Chip label="None" color="default" size="small" />
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                    {provider.w9OnFile && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Description />}
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            const base = process.env.REACT_APP_API_URL
                              ? (process.env.REACT_APP_API_URL.endsWith('/api') ? process.env.REACT_APP_API_URL : process.env.REACT_APP_API_URL + '/api')
                              : window.location.origin + '/api';
                            const r = await fetch(`${base}/compliance/provider/${provider._id}/w9-document`, {
                              headers: { Authorization: `Bearer ${token}` },
                            });
                            if (!r.ok) throw new Error('Download failed');
                            const blob = await r.blob();
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `W-9-${provider.businessName || provider._id}.pdf`;
                            a.click();
                            window.URL.revokeObjectURL(url);
                          } catch (e) {
                            setError('Failed to download W-9 document');
                          }
                        }}
                      >
                        W-9 doc
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Assignment />}
                      onClick={() => setSample1099Dialog({ provider, year: new Date().getFullYear() })}
                    >
                      Sample 1099
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleEnableBackupWithholding(provider._id)}
                      title="Re-check TIN and update backup withholding status (24% if TIN missing/invalid)"
                    >
                      Update backup withholding
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Generate 1099 Dialog */}
      <Dialog open={generate1099Dialog} onClose={() => setGenerate1099Dialog(false)}>
        <DialogTitle>Generate 1099-NEC Forms</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will generate and email 1099-NEC forms to all providers who earned $600 or more in the selected year.
          </Typography>
          <Typography variant="body2">
            Year: {generate1099Year}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerate1099Dialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleGenerate1099}
            disabled={generating1099}
            startIcon={generating1099 ? <CircularProgress size={20} /> : <Send />}
          >
            {generating1099 ? 'Generating...' : 'Generate & Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sample 1099-NEC Dialog */}
      <Dialog open={!!sample1099Dialog} onClose={() => setSample1099Dialog(null)}>
        <DialogTitle>Sample 1099-NEC</DialogTitle>
        <DialogContent>
          {sample1099Dialog && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Download a preview 1099-NEC for this provider and year. Uses actual W-9 and payment data; not saved or emailed.
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Provider: {sample1099Dialog.provider.businessName || `${sample1099Dialog.provider.firstName} ${sample1099Dialog.provider.lastName}`}
              </Typography>
              <FormControl fullWidth size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Year</InputLabel>
                <Select
                  value={sample1099Dialog.year}
                  label="Year"
                  onChange={(e) => setSample1099Dialog({ ...sample1099Dialog, year: Number(e.target.value) })}
                >
                  {[new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2].map((y) => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSample1099Dialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={sample1099Loading ? <CircularProgress size={20} /> : <FileDownload />}
            disabled={sample1099Loading || !sample1099Dialog}
            onClick={async () => {
              if (!sample1099Dialog) return;
              setSample1099Loading(true);
              setError('');
              try {
                const token = localStorage.getItem('token');
                const base = process.env.REACT_APP_API_URL
                  ? (process.env.REACT_APP_API_URL.endsWith('/api') ? process.env.REACT_APP_API_URL : process.env.REACT_APP_API_URL + '/api')
                  : window.location.origin + '/api';
                const r = await fetch(
                  `${base}/compliance/provider/${sample1099Dialog.provider._id}/1099-sample/${sample1099Dialog.year}`,
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                if (!r.ok) throw new Error('Failed to generate sample');
                const blob = await r.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `1099-NEC-sample-${sample1099Dialog.provider.businessName || sample1099Dialog.provider._id}-${sample1099Dialog.year}.pdf`;
                a.click();
                window.URL.revokeObjectURL(url);
                setSample1099Dialog(null);
              } catch (e: any) {
                setError(e.message || 'Failed to download sample 1099');
              } finally {
                setSample1099Loading(false);
              }
            }}
          >
            {sample1099Loading ? 'Generating...' : 'Download sample'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export for 1099 preparer dialog */}
      <Dialog open={export1099PreparerDialog} onClose={() => setExport1099PreparerDialog(false)}>
        <DialogTitle>Export for 1099 preparer</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Download a CSV with payer info and, for each provider with W-9 on file: recipient name, address, TIN, Box 1 (nonemployee compensation), Box 4 (federal tax withheld) for the selected year. Give this file to your accountant or firm to prepare 1099-NECs.
          </Typography>
          <FormControl fullWidth size="small" sx={{ minWidth: 120, mt: 1 }}>
            <InputLabel>Tax year</InputLabel>
            <Select
              value={export1099PreparerYear}
              label="Tax year"
              onChange={(e) => setExport1099PreparerYear(Number(e.target.value))}
            >
              {[new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2].map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExport1099PreparerDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={export1099PreparerLoading ? <CircularProgress size={20} /> : <FileDownload />}
            disabled={export1099PreparerLoading}
            onClick={async () => {
              setExport1099PreparerLoading(true);
              setError('');
              try {
                const token = localStorage.getItem('token');
                const base = process.env.REACT_APP_API_URL
                  ? (process.env.REACT_APP_API_URL.endsWith('/api') ? process.env.REACT_APP_API_URL : process.env.REACT_APP_API_URL + '/api')
                  : window.location.origin + '/api';
                const r = await fetch(
                  `${base}/compliance/export-1099-preparer?year=${export1099PreparerYear}`,
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                if (!r.ok) {
                  const err = await r.json().catch(() => ({}));
                  throw new Error(err.error || 'Export failed');
                }
                const blob = await r.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `1099-preparer-export-${export1099PreparerYear}.csv`;
                a.click();
                window.URL.revokeObjectURL(url);
                setExport1099PreparerDialog(false);
              } catch (e: any) {
                setError(e.message || 'Failed to download export');
              } finally {
                setExport1099PreparerLoading(false);
              }
            }}
          >
            {export1099PreparerLoading ? 'Generating...' : 'Download CSV'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CompliancePage;

